import React, { useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../../../context/ThemeContext';

const menuItems = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/projects' },
    { name: 'Certificates', path: '/certificates' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
];

// Same feel as the rest of the site: soft spring, light stagger
const panelVariants = {
    hidden: { opacity: 0, x: 24 },
    show: {
        opacity: 1,
        x: 0,
        transition: {
            type: 'spring',
            stiffness: 220,
            damping: 26,
            staggerChildren: 0.06,
            delayChildren: 0.05,
        },
    },
    exit: { opacity: 0, x: 24, transition: { duration: 0.2 } },
};

const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', stiffness: 120, damping: 16 },
    },
    exit: { opacity: 0, transition: { duration: 0.1 } },
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const SectionLabel = ({ children }) => (
    <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/50">
        {children}
    </h4>
);

const Chip = ({ children, onClick, active }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={`rounded-lg border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 focus:outline-none focus-visible:border-white/50 ${
            active
                ? 'border-white/40 bg-white/10 text-white'
                : 'border-white/10 bg-white/[0.03] text-white/70 hover:border-white/30 hover:bg-white/[0.06] hover:text-white'
        }`}
    >
        {children}
    </button>
);

const SideMenu = ({ menuOpen, setMenuOpen, themes, changeTheme, particleOptions, particleOption }) => {
    const { pathname } = useLocation();
    const { theme: currentTheme } = useContext(ThemeContext);

    // Close on Escape
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [menuOpen, setMenuOpen]);

    return (
        <AnimatePresence>
            {menuOpen && (
                <motion.nav
                    id="mobile-menu"
                    aria-label="Mobile navigation"
                    variants={panelVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    className="fixed inset-x-0 bottom-0 top-[4.45rem] z-20 overflow-auto bg-black/85 px-6 pb-12 pt-4 text-white backdrop-blur-md md:hidden"
                >
                    {/* Primary navigation */}
                    <ul>
                        {menuItems.map((item, i) => {
                            const active = pathname === item.path;
                            return (
                                <motion.li
                                    key={item.name}
                                    variants={itemVariants}
                                    className="border-b border-white/10"
                                >
                                    <Link
                                        to={item.path}
                                        onClick={() => setMenuOpen(false)}
                                        aria-current={active ? 'page' : undefined}
                                        className={`group flex items-center gap-4 py-4 transition-colors duration-200 hover:text-secondary focus:outline-none focus-visible:text-secondary ${
                                            active ? 'text-white' : 'text-white/60'
                                        }`}
                                    >
                                        <span className="w-5 text-xs font-medium tabular-nums text-white/30">
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <span className="text-xl font-semibold tracking-tight">
                                            {item.name}
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className={`ml-auto h-px bg-secondary transition-all duration-300 ${
                                                active
                                                    ? 'w-6 opacity-90'
                                                    : 'w-0 opacity-0 group-hover:w-6 group-hover:opacity-70'
                                            }`}
                                        />
                                    </Link>
                                </motion.li>
                            );
                        })}
                    </ul>

                    {/* Theme switcher */}
                    <motion.div variants={itemVariants} className="mt-9">
                        <SectionLabel>Theme</SectionLabel>
                        <div className="flex flex-wrap gap-2">
                            {Object.keys(themes).map((themeName) => (
                                <Chip
                                    key={themeName}
                                    active={themeName === currentTheme}
                                    onClick={() => {
                                        changeTheme(themeName, null);
                                        setMenuOpen(false);
                                    }}
                                >
                                    {capitalize(themeName)}
                                </Chip>
                            ))}
                        </div>
                    </motion.div>

                    {/* Particle switcher */}
                    <motion.div variants={itemVariants} className="mt-7">
                        <SectionLabel>Particles</SectionLabel>
                        <div className="flex flex-wrap gap-2">
                            {Object.keys(particleOptions).map((optionName) => (
                                <Chip
                                    key={optionName}
                                    active={optionName === particleOption}
                                    onClick={() => {
                                        changeTheme(null, optionName);
                                        setMenuOpen(false);
                                    }}
                                >
                                    {capitalize(optionName)}
                                </Chip>
                            ))}
                        </div>
                    </motion.div>
                </motion.nav>
            )}
        </AnimatePresence>
    );
};

export default SideMenu;
